/**
 * A small expression parser used to grade algebraic answers.
 *
 * Equivalence is decided numerically: two expressions are equal when they agree
 * at many pseudo-random sample points. This accepts `2(x+3)`, `2x+6` and
 * `6+x+x` as the same answer without needing a full computer-algebra system,
 * which is far more than the K–12 slice requires and stays under 200 lines.
 */

export type Node =
  | { kind: 'number'; value: number }
  | { kind: 'variable'; name: string }
  | { kind: 'unary'; op: '-'; operand: Node }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/' | '^'; left: Node; right: Node };

type Token =
  | { type: 'number'; value: number }
  | { type: 'identifier'; value: string }
  | { type: 'operator'; value: string }
  | { type: 'paren'; value: '(' | ')' };

const OPERATORS = new Set(['+', '-', '*', '/', '^']);

export class ParseError extends Error {}

function tokenize(input: string): Token[] {
  const text = input
    .replace(/\\times|\\cdot/g, '*')
    .replace(/\\div/g, '/')
    .replace(/[×·]/g, '*')
    .replace(/÷/g, '/')
    .replace(/\s+/g, '');
  const tokens: Token[] = [];
  let i = 0;

  while (i < text.length) {
    const char = text[i] as string;

    if (/[0-9.]/.test(char)) {
      let j = i;
      while (j < text.length && /[0-9.]/.test(text[j] as string)) j += 1;
      const raw = text.slice(i, j);
      const value = Number(raw);
      if (!Number.isFinite(value)) throw new ParseError(`Not a number: ${raw}`);
      tokens.push({ type: 'number', value });
      i = j;
      continue;
    }

    if (/[a-zA-Z]/.test(char)) {
      tokens.push({ type: 'identifier', value: char });
      i += 1;
      continue;
    }

    if (OPERATORS.has(char)) {
      tokens.push({ type: 'operator', value: char });
      i += 1;
      continue;
    }

    if (char === '(' || char === ')') {
      tokens.push({ type: 'paren', value: char });
      i += 1;
      continue;
    }

    throw new ParseError(`Unexpected character: ${char}`);
  }

  return insertImplicitMultiplication(tokens);
}

/** Turns `2x`, `3(x+1)` and `(x+1)(x-1)` into explicit products. */
function insertImplicitMultiplication(tokens: Token[]): Token[] {
  const out: Token[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i] as Token;
    const next = tokens[i + 1];
    out.push(token);
    if (!next) continue;
    const endsValue =
      token.type === 'number' || token.type === 'identifier' || (token.type === 'paren' && token.value === ')');
    const startsValue =
      next.type === 'number' || next.type === 'identifier' || (next.type === 'paren' && next.value === '(');
    if (endsValue && startsValue) out.push({ type: 'operator', value: '*' });
  }
  return out;
}

/** Recursive-descent parser: `expr → term → factor → power → primary`. */
export function parseExpression(input: string): Node {
  const tokens = tokenize(input);
  let pos = 0;

  const peek = (): Token | undefined => tokens[pos];
  const eat = (): Token => {
    const token = tokens[pos];
    if (!token) throw new ParseError('Unexpected end of expression');
    pos += 1;
    return token;
  };

  function parseSum(): Node {
    let left = parseProduct();
    for (;;) {
      const token = peek();
      if (token?.type !== 'operator' || (token.value !== '+' && token.value !== '-')) break;
      eat();
      left = { kind: 'binary', op: token.value, left, right: parseProduct() };
    }
    return left;
  }

  function parseProduct(): Node {
    let left = parseUnary();
    for (;;) {
      const token = peek();
      if (token?.type !== 'operator' || (token.value !== '*' && token.value !== '/')) break;
      eat();
      left = { kind: 'binary', op: token.value, left, right: parseUnary() };
    }
    return left;
  }

  function parseUnary(): Node {
    const token = peek();
    if (token?.type === 'operator' && token.value === '-') {
      eat();
      return { kind: 'unary', op: '-', operand: parseUnary() };
    }
    if (token?.type === 'operator' && token.value === '+') {
      eat();
      return parseUnary();
    }
    return parsePower();
  }

  function parsePower(): Node {
    const base = parsePrimary();
    const token = peek();
    if (token?.type === 'operator' && token.value === '^') {
      eat();
      // Right-associative: 2^3^2 is 2^(3^2).
      return { kind: 'binary', op: '^', left: base, right: parseUnary() };
    }
    return base;
  }

  function parsePrimary(): Node {
    const token = eat();
    if (token.type === 'number') return { kind: 'number', value: token.value };
    if (token.type === 'identifier') return { kind: 'variable', name: token.value };
    if (token.type === 'paren' && token.value === '(') {
      const inner = parseSum();
      const close = eat();
      if (close.type !== 'paren' || close.value !== ')') throw new ParseError('Expected closing parenthesis');
      return inner;
    }
    throw new ParseError(`Unexpected token: ${JSON.stringify(token)}`);
  }

  if (tokens.length === 0) throw new ParseError('Empty expression');
  const node = parseSum();
  if (pos !== tokens.length) throw new ParseError('Trailing input in expression');
  return node;
}

export function variablesOf(node: Node, found = new Set<string>()): Set<string> {
  switch (node.kind) {
    case 'variable':
      found.add(node.name);
      break;
    case 'unary':
      variablesOf(node.operand, found);
      break;
    case 'binary':
      variablesOf(node.left, found);
      variablesOf(node.right, found);
      break;
    default:
      break;
  }
  return found;
}

export function evaluate(node: Node, scope: Record<string, number> = {}): number {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'variable': {
      const value = scope[node.name];
      if (value === undefined) throw new ParseError(`Unknown variable: ${node.name}`);
      return value;
    }
    case 'unary':
      return -evaluate(node.operand, scope);
    case 'binary': {
      const left = evaluate(node.left, scope);
      const right = evaluate(node.right, scope);
      switch (node.op) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        case '/':
          return left / right;
        case '^':
          return left ** right;
      }
    }
  }
}

/** Deterministic sample points, so grading never flickers between attempts. */
function samplePoints(variables: string[], count: number): Record<string, number>[] {
  const points: Record<string, number>[] = [];
  for (let i = 0; i < count; i += 1) {
    const point: Record<string, number> = {};
    variables.forEach((name, index) => {
      // Irrational-ish spread avoids accidental agreement at nice integers.
      point[name] = 1.7 + i * 0.9137 + index * 0.6180;
    });
    points.push(point);
  }
  return points;
}

/**
 * True when two expressions denote the same function of their variables.
 * Different variable sets are never equivalent: `x + x` is not `2y`.
 */
export function areExpressionsEquivalent(a: string, b: string, tolerance = 1e-9): boolean {
  let left: Node;
  let right: Node;
  try {
    left = parseExpression(a);
    right = parseExpression(b);
  } catch {
    return false;
  }

  const leftVars = [...variablesOf(left)].sort();
  const rightVars = [...variablesOf(right)].sort();
  if (leftVars.join(',') !== rightVars.join(',')) return false;

  const points = samplePoints(leftVars, 8);
  for (const point of points) {
    const lv = evaluate(left, point);
    const rv = evaluate(right, point);
    if (!Number.isFinite(lv) || !Number.isFinite(rv)) continue;
    const scale = Math.max(1, Math.abs(lv), Math.abs(rv));
    if (Math.abs(lv - rv) > tolerance * scale) return false;
  }
  return true;
}

/**
 * Whether an expression is written as a product rather than as a sum.
 *
 * Used to grade "factorise this", where the *form* of the answer is the skill
 * being assessed — the same reason a fraction answer can require lowest terms.
 * Numeric equivalence cannot tell (x+2)(x+3) from x²+5x+6, because they are the
 * same function; only the shape of the expression distinguishes them.
 *
 * Deliberately a shallow check: it asks whether the top-level operation is an
 * addition, not whether every factor is irreducible. It therefore accepts
 * 2(x²+5x+6), which is factored only partway. Catching that needs a real
 * computer-algebra system, and the error this exists to catch — leaving the
 * answer expanded — is caught exactly.
 */
export function isProductForm(input: string): boolean {
  let node: Node;
  try {
    node = parseExpression(input);
  } catch {
    return false;
  }
  // A leading minus wraps the whole product: -(x+1)(x+2) is still factored.
  while (node.kind === 'unary') node = node.operand;
  if (node.kind === 'binary') return node.op !== '+' && node.op !== '-';
  // A bare number or variable is as factored as it can be.
  return true;
}

/** Whether the text parses at all — used to tell "malformed" from "wrong". */
export function isParseable(input: string): boolean {
  try {
    parseExpression(input);
    return true;
  } catch {
    return false;
  }
}
